import { useMemo } from "react";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import {
  InvoiceGroupCardSkeleton,
  LoadMoreButton,
} from "@/accounts-payable/common";
import { useGetAutomatedInvoices } from "@/accounts-payable/query";
import { flattenPages } from "@/accounts-payable/utils";
import { AutomatedInvoiceCard } from "./automated-invoice-card";

export const ApAutomatedTab = ({
  businessId,
  onSelectInvoice,
}: {
  businessId: string | null;
  onSelectInvoice: (invoiceId: string) => void;
}) => {
  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetAutomatedInvoices(businessId);

  const items = useMemo(() => flattenPages(data?.pages), [data]);

  if (isLoading) {
    return (
      <ScrollArea vertical type="scroll" className="h-full min-h-0 w-full">
        <Stack gap="md" fullWidth className="py-4">
          <InvoiceGroupCardSkeleton rows={3} />
          <InvoiceGroupCardSkeleton rows={2} />
        </Stack>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea vertical type="scroll" className="h-full min-h-0 w-full">
      {items.length > 0 ? (
        <Stack gap="md" fullWidth className="py-4">
          {items.map((item) => (
            <AutomatedInvoiceCard
              key={item.id}
              item={item}
              onOpenInvoice={() => onSelectInvoice(item.id)}
            />
          ))}
          {hasNextPage && (
            <Stack fullWidth className="pb-4">
              <LoadMoreButton
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                disabled={isFetching}
              />
            </Stack>
          )}
        </Stack>
      ) : (
        // Serves both "nothing qualified yet" and "automation not enabled" —
        // the frontend has no signal to tell them apart.
        <Stack
          gap="sm"
          align="center"
          fullWidth
          className="mt-4 rounded-lg border border-stroke-tertiary bg-card py-12 text-center"
        >
          <span className="text-lg font-semibold">
            Your automatically handled invoices will show up here
          </span>
          <span className="text-md text-tertiary-text">
            When an invoice can be handled end to end, it&apos;s approved and
            applied automatically — no review needed.
          </span>
        </Stack>
      )}
    </ScrollArea>
  );
};
